import { useState, useEffect, useRef, Fragment, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { apiHelpers } from '../../services/api';
import { useDashboardStore } from '../../store/dashboardStore';
import {
  ArrowPathIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Menu, Popover, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import EditChartModal from '../modals/EditChartModal';

// Date range presets
const DATE_PRESETS = [
  { id: 'today', label: 'Today', getDates: () => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    return { startDate: dateStr, endDate: dateStr };
  }},
  { id: 'yesterday', label: 'Yesterday', getDates: () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = yesterday.toISOString().split('T')[0];
    return { startDate: dateStr, endDate: dateStr };
  }},
  { id: 'last7days', label: 'Last 7 Days', getDates: () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
  }},
  { id: 'last30days', label: 'Last 30 Days', getDates: () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
  }},
  { id: 'thisMonth', label: 'This Month', getDates: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
  }},
  { id: 'lastMonth', label: 'Last Month', getDates: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
  }},
  { id: 'thisYear', label: 'This Year', getDates: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    return { startDate: start.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
  }},
  { id: 'custom', label: 'Custom Range', getDates: () => null }
];

// Export utilities
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }
  
  // Get headers from first row
  const headers = Object.keys(data[0]);
  
  // Build CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values that need quoting (contains comma, newline, or quotes)
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    )
  ].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  toast.success('CSV exported successfully');
};

const exportToExcel = async (data, filename) => {
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }
  
  try {
    // Dynamically import xlsx library
    const XLSX = await import('xlsx');
    
    // Create worksheet from data
    const worksheet = XLSX.utils.json_to_sheet(data);
    
    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    
    // Auto-size columns
    const maxWidth = 50;
    const headers = Object.keys(data[0]);
    worksheet['!cols'] = headers.map(header => {
      const maxLen = Math.max(
        header.length,
        ...data.slice(0, 100).map(row => String(row[header] || '').length)
      );
      return { wch: Math.min(maxLen + 2, maxWidth) };
    });
    
    // Write and download
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    
    toast.success('Excel file exported successfully');
  } catch (error) {
    console.error('Excel export error:', error);
    // Fallback to CSV if xlsx library fails to load
    toast.error('Excel export failed, trying CSV...');
    exportToCSV(data, filename);
  }
};

// Track if world map is registered
let worldMapRegistered = false;

// Function to load and register world map
const registerWorldMap = async () => {
  if (worldMapRegistered) return true;
  
  try {
    // Load world map GeoJSON from a CDN
    const response = await fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json');
    const topology = await response.json();
    
    // Convert TopoJSON to GeoJSON
    const topojson = await import('topojson-client');
    const geojson = topojson.feature(topology, topology.objects.countries);
    
    // Register with ECharts
    echarts.registerMap('world', geojson);
    worldMapRegistered = true;
    console.log('[ChartWidget] World map registered successfully');
    return true;
  } catch (error) {
    console.error('[ChartWidget] Failed to load world map:', error);
    return false;
  }
};

export default function ChartWidget({ chart }) {
  // Immediate debug log on every render
  console.log('[ChartWidget] ======= COMPONENT RENDER =======');
  console.log('[ChartWidget] chart prop:', chart);
  
  const [data, setData] = useState(null);
  const [echartsOption, setEchartsOption] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [fullChart, setFullChart] = useState(chart); // Full chart data with query
  const [mapLoaded, setMapLoaded] = useState(worldMapRegistered);
  const { filters, deleteChart } = useDashboardStore();
  
  // Date range filter state
  const [dateRange, setDateRange] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Check if chart has date filtering enabled
  const hasDateFilter = fullChart?.dateFilterConfig?.enabled && fullChart?.dateFilterConfig?.column;
  
  // Get chart ID and type safely
  const chartId = chart?._id;
  const chartType = fullChart?.type || chart?.type || 'bar';
  
  // Load world map for geo charts
  useEffect(() => {
    if (chartType === 'geo' && !mapLoaded) {
      registerWorldMap().then(success => {
        setMapLoaded(success);
      });
    }
  }, [chartType, mapLoaded]);
  
  // Track mount status to avoid state updates on unmounted component
  const isMountedRef = useRef(true);
  const fetchAttemptRef = useRef(0);
  const abortControllerRef = useRef(null);
  
  // Debug: Log component mount and props
  useEffect(() => {
    console.log('[ChartWidget] Component mounted/updated with chart:', chart);
    console.log('[ChartWidget] chartId:', chartId);
  }, [chart, chartId]);
  
  // Fetch chart data function - used by both useEffect and manual refresh
  const fetchData = useCallback(async (signal) => {
    console.log('[ChartWidget] fetchData called for:', chartId);
    
    if (!chartId) {
      console.error('[ChartWidget] Chart ID is missing, chart prop:', chart);
      if (isMountedRef.current) {
        setError('Chart ID is missing');
        setIsLoading(false);
      }
      return;
    }
    
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }
    
    try {
      console.log('[ChartWidget] Fetching data for chart:', chartId);
      
      // Combine dashboard filters with date range filter
      const combinedFilters = {
        ...filters,
        ...(dateRange ? { _dateRange: dateRange } : {})
      };
      
      const response = await apiHelpers.getChartData(chartId, combinedFilters);
      
      // Check if request was aborted or component unmounted
      if (signal?.aborted || !isMountedRef.current) {
        console.log('[ChartWidget] Request aborted or component unmounted, skipping state update');
        return;
      }
      
      console.log('[ChartWidget] Response received:', response.data);
      
      setData(response.data.data);
      setEchartsOption(response.data.echartsOption);
      // Store the full chart object from the response (includes query)
      if (response.data.chart) {
        setFullChart(response.data.chart);
      }
      // Reset retry counter on success
      fetchAttemptRef.current = 0;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        console.log('[ChartWidget] Fetch was cancelled');
        return;
      }
      
      if (!isMountedRef.current) return;
      
      console.error('[ChartWidget] Chart data fetch error:', err);
      
      // Retry logic for transient errors (up to 2 retries with exponential backoff)
      const attempt = fetchAttemptRef.current;
      if (attempt < 2) {
        fetchAttemptRef.current = attempt + 1;
        const delay = Math.pow(2, attempt) * 500; // 500ms, 1s
        console.log(`[ChartWidget] Retrying fetch in ${delay}ms (attempt ${attempt + 1})`);
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchData(null);
          }
        }, delay);
        return;
      }
      
      setError(err.response?.data?.error || err.message || 'Failed to load chart data');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [chartId, filters, dateRange, chart]);
  
  // Update when chart prop, filters, or date range change
  useEffect(() => {
    isMountedRef.current = true;
    fetchAttemptRef.current = 0;
    
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    // Fetch data
    fetchData(abortControllerRef.current.signal);
    
    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [chartId, filters, dateRange, fetchData]);
  
  // Also update fullChart when chart prop changes (for name, type changes from parent)
  useEffect(() => {
    if (chart) {
      setFullChart(prev => ({ ...prev, ...chart }));
    }
  }, [chart]);
  
  // Manual refresh function - uses the existing fetchData with reset retry counter
  const fetchChartData = useCallback(() => {
    fetchAttemptRef.current = 0;
    fetchData(null);
  }, [fetchData]);
  
  // Handle date preset selection
  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    if (preset.id === 'custom') {
      setShowDatePicker(true);
    } else {
      const dates = preset.getDates();
      if (dates) {
        setDateRange(dates);
        setShowDatePicker(false);
      }
    }
  };
  
  // Handle custom date range apply
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateRange({ startDate: customStartDate, endDate: customEndDate });
      setShowDatePicker(false);
    }
  };
  
  // Clear date filter
  const clearDateFilter = () => {
    setDateRange(null);
    setSelectedPreset(null);
    setCustomStartDate('');
    setCustomEndDate('');
  };
  
  // Format date range for display
  const getDateRangeLabel = () => {
    if (!dateRange) return 'All Time';
    const preset = DATE_PRESETS.find(p => p.id === selectedPreset);
    if (preset && preset.id !== 'custom') return preset.label;
    return `${dateRange.startDate} to ${dateRange.endDate}`;
  };
  
  const handleRefresh = () => {
    console.log('[ChartWidget] Refresh clicked');
    fetchChartData();
    toast.success('Chart refreshed');
  };
  
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this chart?')) {
      try {
        await deleteChart(chart._id);
        toast.success('Chart deleted');
      } catch (err) {
        toast.error('Failed to delete chart');
      }
    }
  };
  
  const handleEdit = () => {
    console.log('[ChartWidget] Edit clicked, fullChart:', fullChart);
    setShowEditModal(true);
  };
  
  // Debug: Log when modal state changes
  useEffect(() => {
    console.log('[ChartWidget] showEditModal changed to:', showEditModal);
  }, [showEditModal]);
  
  const handleChartUpdated = (updatedChart) => {
    setFullChart(updatedChart);
    // Refresh chart data with new query
    fetchChartData();
  };
  
  // Export handlers
  const handleExportCSV = () => {
    const exportFilename = (chartName || 'chart-data').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    exportToCSV(data, exportFilename);
  };
  
  const handleExportExcel = () => {
    const exportFilename = (chartName || 'chart-data').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    exportToExcel(data, exportFilename);
  };
  
  // Get the chart name safely
  const chartName = fullChart?.name || chart?.name || 'Untitled Chart';
  
  // Build ECharts options based on chart type
  const getChartOptions = () => {
    console.log('[ChartWidget] getChartOptions called');
    console.log('[ChartWidget] - data:', data);
    console.log('[ChartWidget] - data length:', data?.length);
    console.log('[ChartWidget] - echartsOption:', echartsOption);
    console.log('[ChartWidget] - chartType:', chartType);
    
    if (!data || data.length === 0) {
      console.log('[ChartWidget] No data available, showing message');
      return {
        title: { 
          text: 'No data available', 
          left: 'center', 
          top: 'center',
          textStyle: { color: '#666' }
        }
      };
    }
    
    // Helper to format field name as label (convert snake_case/camelCase to Title Case)
    const formatLabel = (field) => {
      if (!field) return '';
      return field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/\b\w/g, l => l.toUpperCase())
        .trim();
    };
    
    // Use pre-built options from backend if available and has series
    if (echartsOption && (echartsOption.series || echartsOption.xAxis)) {
      console.log('[ChartWidget] Using backend echartsOption with series');
      
      // Ensure axis labels are present
      const enhancedOption = {
        ...echartsOption,
        animation: true,
        tooltip: { trigger: 'axis', ...(echartsOption.tooltip || {}) }
      };
      
      // Add axis labels if missing
      if (enhancedOption.xAxis && !enhancedOption.xAxis.name) {
        const xField = fullChart?.chartConfig?.xAxis?.field || Object.keys(data[0] || {})[0];
        enhancedOption.xAxis = {
          ...enhancedOption.xAxis,
          name: fullChart?.chartConfig?.xAxis?.label || formatLabel(xField),
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: { fontWeight: 'bold', fontSize: 12 }
        };
      }
      
      if (enhancedOption.yAxis && !enhancedOption.yAxis.name) {
        const yField = fullChart?.chartConfig?.yAxis?.field || 
                       fullChart?.chartConfig?.series?.[0]?.field || 
                       Object.keys(data[0] || {})[1];
        enhancedOption.yAxis = {
          ...enhancedOption.yAxis,
          name: fullChart?.chartConfig?.yAxis?.label || formatLabel(yField),
          nameLocation: 'middle',
          nameGap: 50,
          nameRotate: 90,
          nameTextStyle: { fontWeight: 'bold', fontSize: 12 }
        };
      }
      
      // Adjust grid for labels
      if (enhancedOption.xAxis?.name || enhancedOption.yAxis?.name) {
        enhancedOption.grid = {
          ...enhancedOption.grid,
          left: '12%',
          right: '5%',
          bottom: '15%',
          containLabel: true
        };
      }
      
      return enhancedOption;
    }
    
    console.log('[ChartWidget] Building default options for type:', chartType);
    
    // Build default chart options from data
    const keys = Object.keys(data[0] || {});
    const xField = keys[0];
    const yField = keys.length > 1 ? keys[1] : keys[0];
    
    // Get labels from chart config or generate from field names
    const xLabel = fullChart?.chartConfig?.xAxis?.label || formatLabel(xField);
    const yLabel = fullChart?.chartConfig?.yAxis?.label || formatLabel(yField);
    
    if (['pie', 'donut'].includes(chartType)) {
      return {
        animation: true,
        tooltip: { 
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center'
        },
        series: [{
          type: 'pie',
          radius: chartType === 'donut' ? ['40%', '70%'] : '70%',
          center: ['40%', '50%'],
          label: {
            show: true,
            formatter: '{b}: {d}%'
          },
          data: data.map(row => ({
            name: String(row[xField]),
            value: row[yField]
          }))
        }]
      };
    }
    
    // Heatmap chart
    if (chartType === 'heatmap') {
      // For heatmap, we need x, y, and value columns
      const zField = keys.length > 2 ? keys[2] : yField;
      const zLabel = fullChart?.chartConfig?.zAxis?.label || formatLabel(zField);
      
      // Get unique x and y values
      const xValues = [...new Set(data.map(row => String(row[xField])))];
      const yValues = [...new Set(data.map(row => String(row[yField])))];
      
      // Build heatmap data: [xIndex, yIndex, value]
      const heatmapData = data.map(row => {
        const xIndex = xValues.indexOf(String(row[xField]));
        const yIndex = yValues.indexOf(String(row[yField]));
        const value = row[zField] || row[yField];
        return [xIndex, yIndex, value];
      });
      
      // Calculate min/max for visual map
      const values = heatmapData.map(d => d[2]);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      
      return {
        animation: true,
        tooltip: {
          position: 'top',
          formatter: (params) => {
            const xVal = xValues[params.data[0]];
            const yVal = yValues[params.data[1]];
            const val = params.data[2];
            return `${xLabel}: ${xVal}<br/>${yLabel}: ${yVal}<br/>${zLabel}: ${val}`;
          }
        },
        grid: {
          left: '15%',
          right: '12%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xValues,
          name: xLabel,
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: { fontWeight: 'bold', fontSize: 12 },
          splitArea: { show: true },
          axisLabel: {
            rotate: xValues.length > 10 ? 45 : 0,
            interval: 0
          }
        },
        yAxis: {
          type: 'category',
          data: yValues,
          name: yLabel,
          nameLocation: 'middle',
          nameGap: 50,
          nameRotate: 90,
          nameTextStyle: { fontWeight: 'bold', fontSize: 12 },
          splitArea: { show: true }
        },
        visualMap: {
          min: minValue,
          max: maxValue,
          calculable: true,
          orient: 'vertical',
          right: '2%',
          top: 'center',
          inRange: {
            color: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026']
          }
        },
        series: [{
          name: zLabel,
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: data.length <= 100,
            fontSize: 10
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      };
    }
    
    // Geo Map chart
    if (chartType === 'geo') {
      // Detect if data has coordinates (lat/lng) or region names
      const hasCoordinates = keys.some(k => 
        k.toLowerCase().includes('lat') || 
        k.toLowerCase().includes('lng') || 
        k.toLowerCase().includes('lon') ||
        k.toLowerCase().includes('latitude') ||
        k.toLowerCase().includes('longitude')
      );
      
      // Find the relevant columns
      let latField = keys.find(k => k.toLowerCase().includes('lat'));
      let lngField = keys.find(k => k.toLowerCase().includes('lng') || k.toLowerCase().includes('lon'));
      const nameField = keys.find(k => 
        k.toLowerCase().includes('name') || 
        k.toLowerCase().includes('city') || 
        k.toLowerCase().includes('country') ||
        k.toLowerCase().includes('location') ||
        k.toLowerCase().includes('address')
      ) || xField;
      const valueField = keys.find(k => 
        k.toLowerCase().includes('count') || 
        k.toLowerCase().includes('value') ||
        k.toLowerCase().includes('total') ||
        k.toLowerCase().includes('amount')
      ) || yField;
      
      // Calculate value range for sizing/coloring
      const values = data.map(d => Number(d[valueField]) || 1);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      
      if (hasCoordinates && latField && lngField) {
        // Scatter plot on geo coordinates
        const scatterData = data.map(row => ({
          name: String(row[nameField] || ''),
          value: [
            Number(row[lngField]) || 0,  // longitude first for ECharts
            Number(row[latField]) || 0,  // latitude second
            Number(row[valueField]) || 1
          ]
        })).filter(d => d.value[0] !== 0 || d.value[1] !== 0);
        
        return {
          animation: true,
          tooltip: {
            trigger: 'item',
            formatter: (params) => {
              const { name, value } = params.data;
              return `${name}<br/>Lat: ${value[1].toFixed(4)}<br/>Lng: ${value[0].toFixed(4)}<br/>Value: ${value[2]}`;
            }
          },
          geo: {
            map: 'world',
            roam: true,
            zoom: 1.2,
            center: [110, 0], // Center on Southeast Asia/Indonesia
            itemStyle: {
              areaColor: '#e7e8ea',
              borderColor: '#ccc'
            },
            emphasis: {
              itemStyle: {
                areaColor: '#cfd8dc'
              }
            }
          },
          visualMap: {
            min: minValue,
            max: maxValue,
            calculable: true,
            orient: 'vertical',
            right: 10,
            top: 'center',
            text: ['High', 'Low'],
            inRange: {
              color: ['#50a3ba', '#eac736', '#d94e5d']
            }
          },
          series: [{
            name: formatLabel(valueField),
            type: 'scatter',
            coordinateSystem: 'geo',
            data: scatterData,
            symbolSize: (val) => {
              const size = ((val[2] - minValue) / (maxValue - minValue || 1)) * 30 + 8;
              return Math.max(8, Math.min(size, 50));
            },
            encode: {
              value: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                formatter: '{b}'
              }
            }
          }]
        };
      } else {
        // Fallback: Show as a styled scatter chart representing locations
        // This works when we don't have actual coordinates but have location names
        return {
          animation: true,
          title: {
            text: 'Location Data',
            subtext: 'Add latitude/longitude columns for map view',
            left: 'center',
            textStyle: { fontSize: 14 }
          },
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c}'
          },
          grid: {
            left: '10%',
            right: '10%',
            bottom: '15%',
            top: '20%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: data.map(row => String(row[nameField])),
            name: formatLabel(nameField),
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: {
              rotate: data.length > 5 ? 45 : 0,
              interval: 0
            }
          },
          yAxis: {
            type: 'value',
            name: formatLabel(valueField),
            nameLocation: 'middle',
            nameGap: 50
          },
          series: [{
            type: 'bar',
            data: data.map(row => ({
              name: String(row[nameField]),
              value: Number(row[valueField]) || 0,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: '#83bff6' },
                    { offset: 0.5, color: '#188df0' },
                    { offset: 1, color: '#188df0' }
                  ]
                },
                borderRadius: [4, 4, 0, 0]
              }
            })),
            emphasis: {
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: '#2378f7' },
                    { offset: 0.7, color: '#2378f7' },
                    { offset: 1, color: '#83bff6' }
                  ]
                }
              }
            }
          }]
        };
      }
    }
    
    // Default bar/line/area chart with axis labels
    return {
      animation: true,
      tooltip: { 
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      grid: { 
        left: '12%', 
        right: '5%', 
        bottom: '15%', 
        top: '10%',
        containLabel: true 
      },
      xAxis: {
        type: 'category',
        data: data.map(row => String(row[xField])),
        name: xLabel,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { 
          fontWeight: 'bold', 
          fontSize: 12 
        },
        axisLabel: {
          rotate: data.length > 10 ? 45 : 0,
          interval: 0
        }
      },
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: 50,
        nameRotate: 90,
        nameTextStyle: { 
          fontWeight: 'bold', 
          fontSize: 12 
        }
      },
      series: [{
        name: yLabel,
        type: chartType === 'area' ? 'line' : (chartType || 'bar'),
        data: data.map(row => row[yField]),
        areaStyle: chartType === 'area' ? {} : undefined,
        itemStyle: {
          borderRadius: chartType === 'bar' ? [4, 4, 0, 0] : undefined
        }
      }]
    };
  };
  
  // Render KPI card
  const renderKPI = () => {
    const value = data?.[0]?.[fullChart?.chartConfig?.kpiConfig?.valueField] || 0;
    const formattedValue = new Intl.NumberFormat().format(value);
    
    return (
      <div className="h-full flex flex-col p-4">
        <div className="flex items-center justify-between mb-2 drag-handle cursor-move">
          <h3 className="font-medium text-surface-700 dark:text-surface-300 text-sm">
            {chartName}
          </h3>
          <ChartMenu 
            onRefresh={handleRefresh} 
            onEdit={handleEdit} 
            onDelete={handleDelete}
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            hasData={data && data.length > 0}
          />
        </div>
        
        <div className="flex-1 flex flex-col justify-center">
          {isLoading ? (
            <div className="skeleton h-12 w-32 mx-auto" />
          ) : error ? (
            <div className="text-sm text-red-500 text-center">{error}</div>
          ) : (
            <>
              <div className="text-3xl font-display font-bold text-surface-900 dark:text-white text-center">
                {fullChart?.chartConfig?.kpiConfig?.prefix}
                {formattedValue}
                {fullChart?.chartConfig?.kpiConfig?.suffix}
              </div>
              {fullChart?.chartConfig?.kpiConfig?.comparisonLabel && (
                <div className="text-sm text-surface-500 text-center mt-1">
                  {fullChart.chartConfig.kpiConfig.comparisonLabel}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };
  
  // Render table
  const renderTable = () => (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700 drag-handle cursor-move">
        <h3 className="font-medium text-surface-900 dark:text-white">
          {chartName}
        </h3>
        <ChartMenu 
          onRefresh={handleRefresh} 
          onEdit={handleEdit} 
          onDelete={handleDelete}
          onExportCSV={handleExportCSV}
          onExportExcel={handleExportExcel}
          hasData={data && data.length > 0}
        />
      </div>
      
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="skeleton h-8" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">{error}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-50 dark:bg-surface-800">
              <tr>
                {data?.[0] && Object.keys(data[0]).map(key => (
                  <th key={key} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.slice(0, 100).map((row, i) => (
                <tr key={i} className="border-b border-surface-100 dark:border-surface-800">
                  {Object.values(row).map((value, j) => (
                    <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300">
                      {String(value)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
  
  // Render standard chart
  // Date picker component for chart header
  const renderDatePicker = () => {
    if (!hasDateFilter) return null;
    
    return (
      <Popover className="relative">
        {({ open }) => (
          <>
            <div className="flex items-center gap-1">
              <Popover.Button className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-600 transition-colors">
                <CalendarDaysIcon className="w-3.5 h-3.5" />
                <span className="max-w-[100px] truncate">{getDateRangeLabel()}</span>
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
              </Popover.Button>
              
              {dateRange && (
                <button
                  onClick={clearDateFilter}
                  className="p-1 rounded hover:bg-surface-200 dark:hover:bg-surface-600 transition-colors"
                  title="Clear date filter"
                >
                  <XMarkIcon className="w-3 h-3 text-surface-500" />
                </button>
              )}
            </div>
            
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute right-0 z-50 mt-2 w-64 bg-white dark:bg-surface-800 rounded-lg shadow-xl border border-surface-200 dark:border-surface-700 p-3">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-surface-500 uppercase tracking-wide mb-2">Date Range</p>
                  
                  {/* Preset buttons */}
                  <div className="grid grid-cols-2 gap-1">
                    {DATE_PRESETS.filter(p => p.id !== 'custom').map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => handlePresetSelect(preset)}
                        className={`px-2 py-1.5 text-xs rounded-md transition-colors ${
                          selectedPreset === preset.id
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                            : 'bg-surface-50 dark:bg-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-600'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Custom range */}
                  <div className="pt-2 border-t border-surface-200 dark:border-surface-700">
                    <p className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-2">Custom Range</p>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700 text-surface-900 dark:text-white"
                        />
                        <span className="text-surface-400 self-center">-</span>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700 text-surface-900 dark:text-white"
                        />
                      </div>
                      <button
                        onClick={handleApplyCustomRange}
                        disabled={!customStartDate || !customEndDate}
                        className="w-full px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Apply Custom Range
                      </button>
                    </div>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };
  
  const renderChart = () => (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700 drag-handle cursor-move">
        <h3 className="font-medium text-surface-900 dark:text-white truncate flex-1 mr-2">
          {chartName}
        </h3>
        <div className="flex items-center gap-2">
          {renderDatePicker()}
          <ChartMenu 
            onRefresh={handleRefresh} 
            onEdit={handleEdit} 
            onDelete={handleDelete}
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            hasData={data && data.length > 0}
          />
        </div>
      </div>
      
      <div className="flex-1 p-2">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-500 text-sm px-4 text-center">
            {error}
          </div>
        ) : (
          <ReactECharts
            option={getChartOptions()}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        )}
      </div>
    </div>
  );
  
  // Debug: Log render state
  console.log('[ChartWidget] Render state - isLoading:', isLoading, 'error:', error, 'data:', data?.length, 'chartType:', chartType);
  
  // Main render - always includes the modal at the end
  return (
    <>
      {chartType === 'kpi' && renderKPI()}
      {chartType === 'table' && renderTable()}
      {!['kpi', 'table'].includes(chartType) && renderChart()}
      
      {/* Edit Modal - rendered via portal to document.body to avoid overflow issues */}
      {showEditModal && createPortal(
        <EditChartModal
          isOpen={showEditModal}
          onClose={() => {
            console.log('[ChartWidget] Closing edit modal');
            setShowEditModal(false);
          }}
          chart={fullChart}
          onChartUpdated={handleChartUpdated}
        />,
        document.body
      )}
    </>
  );
}

function ChartMenu({ onRefresh, onEdit, onDelete, onExportCSV, onExportExcel, hasData }) {
  // Stop propagation to prevent react-grid-layout drag from interfering
  const handleClick = (e, callback) => {
    e.stopPropagation();
    e.preventDefault();
    callback?.();
  };

  return (
    <Menu as="div" className="relative" onClick={(e) => e.stopPropagation()}>
      <Menu.Button 
        className="p-1 rounded hover:bg-surface-100 dark:hover:bg-surface-800"
        onClick={(e) => e.stopPropagation()}
      >
        <EllipsisVerticalIcon className="w-5 h-5 text-surface-500" />
      </Menu.Button>
      <Menu.Items className="absolute right-0 mt-1 w-48 bg-white dark:bg-surface-800 rounded-lg shadow-lg border border-surface-200 dark:border-surface-700 z-50 py-1">
        <button
          type="button"
          onClick={(e) => handleClick(e, onEdit)}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-700 dark:text-surface-200 text-left hover:bg-surface-100 dark:hover:bg-surface-700"
        >
          <PencilSquareIcon className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">Edit</span>
        </button>
        <button
          type="button"
          onClick={(e) => handleClick(e, onRefresh)}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-700 dark:text-surface-200 text-left hover:bg-surface-100 dark:hover:bg-surface-700"
        >
          <ArrowPathIcon className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">Refresh</span>
        </button>
        
        {/* Export Section */}
        {hasData && (
          <>
            <div className="border-t border-surface-200 dark:border-surface-700 my-1" />
            <div className="px-4 py-1">
              <span className="text-xs font-medium text-surface-400 uppercase tracking-wide">Export</span>
            </div>
            <button
              type="button"
              onClick={(e) => handleClick(e, onExportCSV)}
              onMouseDown={(e) => e.stopPropagation()}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-700 dark:text-surface-200 text-left hover:bg-surface-100 dark:hover:bg-surface-700"
            >
              <DocumentTextIcon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">Export as CSV</span>
            </button>
            <button
              type="button"
              onClick={(e) => handleClick(e, onExportExcel)}
              onMouseDown={(e) => e.stopPropagation()}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-700 dark:text-surface-200 text-left hover:bg-surface-100 dark:hover:bg-surface-700"
            >
              <TableCellsIcon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">Export as Excel</span>
            </button>
          </>
        )}
        
        <div className="border-t border-surface-200 dark:border-surface-700 my-1" />
        <button
          type="button"
          onClick={(e) => handleClick(e, onDelete)}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 text-left hover:bg-red-50 dark:hover:bg-red-950"
        >
          <TrashIcon className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">Delete</span>
        </button>
      </Menu.Items>
    </Menu>
  );
}

